import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiPlus, FiEdit, FiTrash2, FiEye, FiUsers, FiX, FiAlertTriangle } from 'react-icons/fi';
import resourceAllocationService from '../../services/resourceAllocationService';
import projectService from '../../services/projectService';
import './ResourceAllocationList.css';
import CommonModal from '../../services/CommonModal';

const ResourceAllocationList = ({ projectId = null, userId = null, showStatistics = true }) => {
  const navigate = useNavigate();
  const [allocations, setAllocations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const confirmDelete = () => {
    handleDelete(selectedAllocation.id);
    setShowDeleteModal(false);
    setSelectedAllocation(null);
  }
  const [filters, setFilters] = useState({
    search: '',
    project_id: projectId || '',
    user_id: userId || '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadAllocations();
    if (showStatistics) {
      loadUtilization();
    }
  }, [filters, pagination.page, projectId, userId]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 100 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadAllocations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        project_id: projectId,
        user_id: userId,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });

      const data = await resourceAllocationService.getAll(params);
      setAllocations(data.data || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      console.error('Error loading allocations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUtilization = async () => {
    try {
      const params = {};
      if (userId) params.user_id = userId;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const data = await resourceAllocationService.getUtilization(params);
      setStatistics(data.data);
    } catch (error) {
      console.error('Error loading utilization:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      project_id: projectId || '',
      user_id: userId || '',
      start_date: '',
      end_date: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {

    try {
      await resourceAllocationService.delete(id);
      loadAllocations();
      if (showStatistics) loadUtilization();
    } catch (error) {
      console.error('Error deleting allocation:', error);
      alert('Failed to delete allocation');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="resource-allocation-list">
      {showStatistics && statistics && (
        <div className="statistics-cards">
          <div className="stat-card">
            <div className="stat-icon">
              <FiUsers />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.total_resources || 0}</div>
              <div className="stat-label">Total Resources</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <FiUsers />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.fully_allocated || 0}</div>
              <div className="stat-label">Fully Allocated</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning">
              <FiAlertTriangle />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.over_allocated || 0}</div>
              <div className="stat-label">Over-allocated</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon info">
              <FiUsers />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.average_utilization || 0}%</div>
              <div className="stat-label">Avg Utilization</div>
            </div>
          </div>
        </div>
      )}

      <div className="list-header">
        <div className="header-left">
          <h2>Resource Allocations</h2>
          <span className="count">{pagination.total} total</span>
        </div>
        <div className="header-right">
          <button className="btn-icon" onClick={() => setShowFilters(!showFilters)}>
            <FiFilter />
          </button>
          {!projectId && !userId && (
            <Link to="/app/resources/allocations/new" className="btn btn-primary">
              <FiPlus /> New Allocation
            </Link>
          )}
        </div>
      </div>
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Allocation"
        message="Are you sure you want to delete this allocation?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedAllocation(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            {!projectId && (
              <div className="form-group">
                <label>Project</label>
                <select name="project_id" value={filters.project_id} onChange={handleFilterChange}>
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Start Date From</label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>End Date To</label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filters-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <FiX /> Clear Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading allocations...</div>
      ) : allocations.length === 0 ? (
        <div className="empty-state">
          <FiUsers size={48} />
          <h3>No Allocations Found</h3>
          <p>Start by creating a new resource allocation</p>
          {!projectId && !userId && (
            <Link to="/app/resources/allocations/new" className="btn btn-primary">
              <FiPlus /> Create Allocation
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  {!projectId && <th>Project</th>}
                  <th>Role</th>
                  <th>Allocation</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Billable Rate</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map(allocation => {
                  const status = resourceAllocationService.getAllocationStatus(allocation);
                  const isOverAllocated = allocation.allocation_percentage > 100;

                  return (
                    <tr key={allocation.id}>
                      <td>
                        <div className="user-cell">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                            {}
                            {allocation.user?.avatar_url ? (
                              <img
                                src={allocation.user.avatar_url}
                                alt={`${allocation.user.first_name} ${allocation.user.last_name}`}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <>
                                {allocation.user?.first_name?.[0]}
                                {allocation.user?.last_name?.[0]}
                              </>
                            )}
                          </div>

                          <div>
                            <div className="user-name">
                              {allocation.user?.first_name} {allocation.user?.last_name}
                            </div>
                            <div className="user-email">
                              {allocation.user?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      {!projectId && (
                        <td>
                          <Link to={`/app/projects/${allocation.project?.id}`} className="project-link">
                            {allocation.project?.name}
                          </Link>
                        </td>
                      )}
                      <td>{allocation.role || '-'}</td>
                      <td>
                        <div className="allocation-cell">
                          <span className={`allocation-badge ${resourceAllocationService.getUtilizationBadgeColor(allocation.allocation_percentage)}`}>
                            {allocation.allocation_percentage}%
                          </span>
                          {isOverAllocated && (
                            <FiAlertTriangle className="warning-icon" title="Over-allocated" />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="date-range">
                          <div>{resourceAllocationService.formatDate(allocation.start_date)}</div>
                          <div className="date-separator">→</div>
                          <div>{allocation.end_date ? resourceAllocationService.formatDate(allocation.end_date) : 'Ongoing'}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${resourceAllocationService.getAllocationStatusColor(status)}`}>
                          {status}
                        </span>
                      </td>
                      <td>
                        {allocation.billable_rate ? `₹${allocation.billable_rate}/hr` : '-'}
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            className="btn-icon"
                            onClick={() => navigate(`/app/resources/allocations/${allocation.id}/edit`)}
                            title="Edit"
                          >
                            <FiEdit className='text-blue-600 hover:text-blue-900' />
                          </button>
                          <button
                            className="btn-icon danger text-red-600 hover:text-red-900"
                            onClick={() => {
                              setSelectedAllocation(allocation);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <FiTrash2 className="danger text-red-600 hover:text-red-900" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-outline-primary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-outline-primary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResourceAllocationList;

